export const formatEPRAnswers = (answers, businessId, existingData = null) => {
  // If existingData is provided, use it to pre-fill default values
  const getDefaultValue = (itemId, questionIndex, type = "value") => {
    // First check if user has already input a value
    if (answers[itemId]?.[questionIndex]) {
      return answers[itemId][questionIndex][type];
    }

    // If no user input, check existing data
    if (!existingData) return null;

    switch (itemId) {
      case 1:
        return questionIndex === 0
          ? existingData.is_symbiotic
            ? "Yes"
            : "No"
          : existingData.is_purchase_all_production
          ? "Yes"
          : "No";

      case 2:
        return questionIndex === 0
          ? existingData.is_leased_fuel
            ? "Yes"
            : "No"
          : existingData.fuel_leased_vehicle?.toString() || null;

      case 3:
        return questionIndex === 0
          ? existingData.ghg_compliance
            ? "Yes"
            : "No"
          : existingData.ghg_compliance_percentage?.toString() || null;

      case 4:
        return questionIndex === 0
          ? existingData.is_leased_fuel_combustion
            ? "Yes"
            : "No"
          : existingData.fuel_leased_combustion?.toString() || null;

      case 5:
        return questionIndex === 0
          ? existingData.is_combustion_ghg_compliance
            ? "Yes"
            : "No"
          : existingData.combustion_ghg_compliance?.toString() || null;

      case 6:
        return questionIndex === 0
          ? existingData.is_leased_refrigerant
            ? "Yes"
            : "No"
          : existingData.leased_proportion_refrigerant?.toString() || null;

      case 7:
        return existingData.leased_proportion_suppresant?.toString() || null;

      case 8: // Incoming transport
      case 9: // Outgoing transport
      case 10: // Travel transport
        const transportCategory =
          itemId === 8 ? "incoming" : itemId === 9 ? "outgoing" : "travel";

        const transportModes = existingData.transport_modes
          ?.filter((mode) => mode.category === transportCategory)
          ?.sort(
            (a, b) =>
              ["car", "van", "truck", "train", "plane", "ship"].indexOf(
                a.mode
              ) -
              ["car", "van", "truck", "train", "plane", "ship"].indexOf(b.mode)
          );

        return transportModes
          ? transportModes.map((mode) => mode.percentage.toString())
          : null;

      case 11:
        return (
          existingData.surplus_materials
            ?.sort(
              (a, b) =>
                ["landfill", "recycling", "incineration", "compost"].indexOf(
                  a.mode
                ) -
                ["landfill", "recycling", "incineration", "compost"].indexOf(
                  b.mode
                )
            )
            ?.map((material) => material.percentage.toString()) || null
        );

      default:
        return null;
    }
  };

  // Modify the existing formatting logic to use getDefaultValue
  const formattedAnswers = {
    business: businessId,
    is_symbiotic: getDefaultValue(1, 0) === "Yes",
    is_purchase_all_production: getDefaultValue(1, 1) === "Yes",
    is_leased_fuel: getDefaultValue(2, 0) === "Yes",
    fuel_leased_vehicle: parseFloat(getDefaultValue(2, 1)) || null,
    ghg_compliance: getDefaultValue(3, 0) === "Yes",
    ghg_compliance_percentage:
      parseFloat(getDefaultValue(3, 0, "inputValue")) || null,
    is_fuel_biomass: getDefaultValue(3, 1) === "Yes",
    is_leased_fuel_combustion: getDefaultValue(4, 0) === "Yes",
    fuel_leased_combustion: parseFloat(getDefaultValue(4, 1)) || null,
    is_combustion_ghg_compliance: getDefaultValue(5, 0) === "Yes",
    combustion_ghg_compliance:
      parseFloat(getDefaultValue(5, 0, "inputValue")) || null,
    is_leased_refrigerant: getDefaultValue(6, 0) === "Yes",
    leased_proportion_refrigerant:
      parseFloat(getDefaultValue(6, 0, "inputValue")) || null,
    leased_proportion_suppresant: parseFloat(getDefaultValue(7, 0)) || null,
    transport_modes: [
      ...formatTransportModes(answers[8], "incoming"),
      ...formatTransportModes(answers[9], "outgoing"),
      ...formatTransportModes(answers[10], "travel"),
    ],
    surplus_materials: formatSurplusMaterials(answers[11]),
  };

  return formattedAnswers;
};

const formatTransportModes = (answer, category) => {
  if (!answer?.[0]?.value || !Array.isArray(answer[0].value)) {
    return [];
  }

  const transportLabels = ["Car", "Van", "Truck", "Train", "Plane", "Ship"];

  return answer[0].value
    .map((percentage, index) => {
      const label = transportLabels[index];
      if (percentage !== null && percentage !== "" && label) {
        return {
          category,
          mode: label.toLowerCase(),
          percentage: parseFloat(percentage) || 0,
        };
      }
      return null;
    })
    .filter((item) => item !== null);
};

const formatSurplusMaterials = (answer) => {
  if (!answer?.[0]?.value || !Array.isArray(answer[0].value)) {
    return [];
  }

  const surplusLabels = ["Landfill", "Recycling", "Incineration", "Compost"];

  return answer[0].value
    .map((percentage, index) => {
      const label = surplusLabels[index];
      if (percentage !== null && percentage !== "" && label) {
        return {
          mode: label.toLowerCase(),
          percentage: parseFloat(percentage) || 0,
        };
      }
      return null;
    })
    .filter((item) => item !== null);
};
